import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { FileSignature, Send, Smartphone, Mail, Upload, ExternalLink, CheckCircle2, Clock, Copy } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const DOC_TYPE_OPTIONS = [
  { value: 'devis', label: '💼 Devis' },
  { value: 'bon_commande', label: '📋 Bon de commande' },
  { value: 'facture', label: '🧾 Facture' },
  { value: 'bon_livraison', label: '📦 Bon de livraison' },
  { value: 'lettre_voiture', label: '🚚 Lettre de voiture' },
  { value: 'bon_reception_chantier', label: '🏗️ Bon de réception chantier' },
  { value: 'pv_fin_chantier', label: '✅ PV fin de chantier' },
  { value: 'contrat', label: '📄 Contrat' },
  { value: 'document_libre', label: '✍️ Autre document' },
];

const FRONTEND_URL = window.location.origin;

export default function SignaturesDashboardPage() {
  const [societes, setSocietes] = useState([]);
  const [selectedSociete, setSelectedSociete] = useState('');
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('tous');
  const [form, setForm] = useState({
    doc_type: 'devis',
    titre: '',
    destinataire_email: '',
    destinataire_nom: '',
    client_nom: '',
    expediteur_nom: '',
    expediteur_email: '',
    montant_ht: '',
    marchandise: '',
    quantite: '',
    unite_quantite: 'qx',
    date_livraison: '',
    lieu_depart: '',
    lieu_arrivee: '',
    message_personnalise: '',
    mode: 'email',
    creer_entree_compta: true,
  });
  const [pdfFile, setPdfFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [lastSignLink, setLastSignLink] = useState(null);

  const loadItems = useCallback(async () => {
    if (!selectedSociete) return;
    try {
      const r = await api.get(`/signatures/?societe_id=${selectedSociete}`);
      setItems(r.data?.items || []);
    } catch (e) { console.error(e); }
  }, [selectedSociete]);

  useEffect(() => {
    (async () => {
      try {
        let arr = [];
        try {
          const r = await api.get('/google/all-societes-status');
          arr = r.data?.societes || [];
        } catch {}
        if (!arr.length) {
          const r2 = await api.get('/tenant/societes');
          arr = r2.data?.societes || r2.data || [];
        }
        setSocietes(arr);
        if (arr.length && !selectedSociete) setSelectedSociete(arr[0].id || arr[0].societe_id);
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadItems(); }, [loadItems]);

  const handleSubmit = async () => {
    if (!form.titre.trim()) { toast.error('Renseigne le titre'); return; }
    if (form.mode === 'email' && !form.destinataire_email) { toast.error('Email destinataire requis'); return; }
    setSubmitting(true);
    setLastSignLink(null);
    try {
      // Si fichier PDF fourni → upload via /terrain/demander-rapide qui accepte multipart
      let result;
      if (pdfFile) {
        const fd = new FormData();
        fd.append('societe_id', selectedSociete);
        fd.append('doc_type', form.doc_type);
        fd.append('titre', form.titre);
        fd.append('client_nom', form.client_nom);
        fd.append('destinataire_email', form.destinataire_email);
        if (form.montant_ht) fd.append('montant_ht', form.montant_ht);
        fd.append('notes', form.message_personnalise || '');
        fd.append('pdf_file', pdfFile);
        const r = await api.post('/signatures/terrain/demander-rapide', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        result = r.data;
        // Si mode email demandé : on redéclenche un demander en JSON pour envoyer
        if (form.mode === 'email' && form.destinataire_email && result.mode === 'face_a_face') {
          // on a déjà le token; on appelle l'endpoint pour envoyer l'email directement
          // pour simplicité on informe que le lien est à partager manuellement
        }
      } else {
        const r = await api.post('/signatures/demander', {
          societe_id: selectedSociete,
          doc_type: form.doc_type,
          titre: form.titre,
          destinataire_email: form.destinataire_email,
          destinataire_nom: form.destinataire_nom,
          client_nom: form.client_nom,
          expediteur_nom: form.expediteur_nom,
          expediteur_email: form.expediteur_email,
          mode_signature: form.mode,
          message_personnalise: form.message_personnalise,
          montant_ht: form.montant_ht ? parseFloat(form.montant_ht) : null,
          marchandise: form.marchandise,
          quantite: form.quantite ? parseFloat(form.quantite) : null,
          unite_quantite: form.unite_quantite,
          date_livraison: form.date_livraison,
          lieu_depart: form.lieu_depart,
          lieu_arrivee: form.lieu_arrivee,
          creer_entree_compta: form.creer_entree_compta,
        });
        result = r.data;
      }
      if (result.success) {
        setLastSignLink(result.sign_link);
        toast.success(form.mode === 'email' ? 'Email envoyé ✉️' : 'Lien de signature prêt 📱');
        setForm({ ...form, titre: '', destinataire_email: '', destinataire_nom: '', client_nom: '', montant_ht: '', message_personnalise: '' });
        setPdfFile(null);
        await loadItems();
      }
    } catch (e) {
      toast.error('Erreur: ' + (e.response?.data?.detail || e.message));
    } finally { setSubmitting(false); }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success('Lien copié');
  };

  const filteredItems = items.filter(it =>
    filter === 'tous' ? true : filter === 'signe' ? it.statut === 'signe' : it.statut !== 'signe'
  );

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="signatures-dashboard-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-emerald-900 flex items-center gap-2">
          <FileSignature className="w-7 h-7" /> Signatures électroniques
        </h1>
        <p className="text-slate-600 mt-1">
          Fais signer n'importe quel document : devis, BL, lettre de voiture, PV chantier, bon de commande…
          À distance (email) ou en face-à-face (téléphone terrain). Archivage auto dans Drive + lien CRM.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Nouvelle demande */}
        <Card className="lg:col-span-1 border-emerald-200">
          <CardHeader className="bg-emerald-50">
            <CardTitle className="flex items-center gap-2 text-emerald-900">
              <Send className="w-5 h-5" /> Nouvelle demande
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 pt-4">
            <div>
              <label className="text-xs font-semibold text-slate-600">Société</label>
              <select value={selectedSociete} onChange={e => setSelectedSociete(e.target.value)}
                      className="w-full mt-1 px-3 py-2 border rounded">
                {societes.map(s => (
                  <option key={s.id || s.societe_id} value={s.id || s.societe_id}>
                    {s.nom || s.societe_nom}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600">Type de document</label>
              <select value={form.doc_type} onChange={e => setForm({ ...form, doc_type: e.target.value })}
                      className="w-full mt-1 px-3 py-2 border rounded" data-testid="doc-type-select">
                {DOC_TYPE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600">Titre *</label>
              <Input value={form.titre} onChange={e => setForm({ ...form, titre: e.target.value })}
                     placeholder="Ex: Devis DV-2026-042 Domaine Bour" data-testid="titre-input" />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600">Client (destinataire marchandise / signataire)</label>
              <Input value={form.client_nom} onChange={e => setForm({ ...form, client_nom: e.target.value })}
                     placeholder="Nom du client" />
            </div>

            {/* Expéditeur (IMPORTANT pour BL) */}
            <div className="p-3 bg-amber-50 rounded border border-amber-200">
              <label className="text-xs font-semibold text-amber-900">📨 Expéditeur (de qui part le document) *</label>
              <Input value={form.expediteur_nom}
                     onChange={e => setForm({ ...form, expediteur_nom: e.target.value })}
                     placeholder="Ex: EARL Vergers Fruits" className="mt-1" data-testid="expediteur-nom" />
              <Input value={form.expediteur_email}
                     onChange={e => setForm({ ...form, expediteur_email: e.target.value })}
                     placeholder="Email expéditeur (optionnel)" className="mt-2 text-xs" />
              <p className="text-[10px] text-amber-700 mt-1">
                Le destinataire saura immédiatement qui lui envoie ce document.
              </p>
            </div>

            {/* Infos spécifiques BL / Lettre de voiture */}
            {(form.doc_type === 'bon_livraison' || form.doc_type === 'lettre_voiture' || form.doc_type === 'bon_reception_chantier') && (
              <div className="p-3 bg-blue-50 rounded border border-blue-200 space-y-2">
                <label className="text-xs font-semibold text-blue-900">📦 Livraison</label>
                <Input value={form.marchandise}
                       onChange={e => setForm({ ...form, marchandise: e.target.value })}
                       placeholder="Marchandise (Blé, HE menthe, fruits...)" className="text-xs" data-testid="marchandise" />
                <div className="grid grid-cols-2 gap-2">
                  <Input type="number" value={form.quantite}
                         onChange={e => setForm({ ...form, quantite: e.target.value })}
                         placeholder="Quantité" className="text-xs" />
                  <select value={form.unite_quantite}
                          onChange={e => setForm({ ...form, unite_quantite: e.target.value })}
                          className="text-xs px-2 py-2 border rounded">
                    <option value="qx">quintaux</option>
                    <option value="kg">kg</option>
                    <option value="t">tonnes</option>
                    <option value="l">litres</option>
                    <option value="u">unités</option>
                  </select>
                </div>
                <Input type="date" value={form.date_livraison}
                       onChange={e => setForm({ ...form, date_livraison: e.target.value })}
                       className="text-xs" />
                <div className="grid grid-cols-2 gap-2">
                  <Input value={form.lieu_depart}
                         onChange={e => setForm({ ...form, lieu_depart: e.target.value })}
                         placeholder="Départ" className="text-xs" />
                  <Input value={form.lieu_arrivee}
                         onChange={e => setForm({ ...form, lieu_arrivee: e.target.value })}
                         placeholder="Arrivée" className="text-xs" />
                </div>
              </div>
            )}

            <div>
              <label className="text-xs font-semibold text-slate-600">Montant HT (€)</label>
              <Input type="number" value={form.montant_ht}
                     onChange={e => setForm({ ...form, montant_ht: e.target.value })}
                     placeholder="Optionnel" />
            </div>

            <label className="flex items-center gap-2 p-2 bg-emerald-50 rounded text-xs cursor-pointer">
              <input type="checkbox" checked={form.creer_entree_compta}
                     onChange={e => setForm({ ...form, creer_entree_compta: e.target.checked })}
                     className="w-4 h-4" />
              <span>Après signature : créer automatiquement entrée compta "à facturer / à livrer"</span>
            </label>

            <div>
              <label className="text-xs font-semibold text-slate-600">PDF du document (optionnel)</label>
              <Input type="file" accept=".pdf" onChange={e => setPdfFile(e.target.files?.[0] || null)} />
              <p className="text-xs text-slate-500 mt-1">
                Si pas de PDF, on génère une attestation de signature minimaliste avec les infos saisies.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setForm({ ...form, mode: 'email' })}
                      className={`p-3 border-2 rounded flex flex-col items-center gap-1 ${form.mode === 'email' ? 'border-emerald-600 bg-emerald-50' : 'border-slate-200'}`}
                      data-testid="mode-email">
                <Mail className="w-5 h-5" />
                <span className="text-xs font-semibold">Par email</span>
              </button>
              <button type="button" onClick={() => setForm({ ...form, mode: 'face_a_face' })}
                      className={`p-3 border-2 rounded flex flex-col items-center gap-1 ${form.mode === 'face_a_face' ? 'border-emerald-600 bg-emerald-50' : 'border-slate-200'}`}
                      data-testid="mode-f2f">
                <Smartphone className="w-5 h-5" />
                <span className="text-xs font-semibold">Face à face</span>
              </button>
            </div>

            {form.mode === 'email' && (
              <>
                <div>
                  <label className="text-xs font-semibold text-slate-600">Email destinataire *</label>
                  <Input type="email" value={form.destinataire_email}
                         onChange={e => setForm({ ...form, destinataire_email: e.target.value })}
                         placeholder="client@exemple.fr" data-testid="email-input" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600">Nom destinataire</label>
                  <Input value={form.destinataire_nom}
                         onChange={e => setForm({ ...form, destinataire_nom: e.target.value })}
                         placeholder="Jean Dupont" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600">Message personnalisé</label>
                  <Textarea rows={2} value={form.message_personnalise}
                            onChange={e => setForm({ ...form, message_personnalise: e.target.value })} />
                </div>
              </>
            )}

            <Button onClick={handleSubmit} disabled={submitting || !form.titre}
                    className="w-full bg-emerald-700 hover:bg-emerald-800"
                    data-testid="demander-signature-btn">
              {submitting ? 'Création...' : form.mode === 'email' ? 'Envoyer par email' : 'Obtenir le lien'}
            </Button>

            {lastSignLink && (
              <div className="mt-3 p-3 bg-emerald-50 rounded border border-emerald-200">
                <p className="text-xs font-semibold text-emerald-900">Lien de signature :</p>
                <div className="flex gap-2 items-center mt-1">
                  <input readOnly value={lastSignLink} className="flex-1 text-xs p-1 border rounded bg-white" />
                  <button onClick={() => copyLink(lastSignLink)} className="text-emerald-700 p-1 hover:bg-emerald-100 rounded">
                    <Copy className="w-4 h-4" />
                  </button>
                  <a href={lastSignLink} target="_blank" rel="noopener noreferrer"
                     className="text-emerald-700 p-1 hover:bg-emerald-100 rounded">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
                <p className="text-[10px] text-emerald-700 mt-2">
                  📱 Face-à-face : ouvre ce lien sur ton téléphone, fais signer le client directement.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Liste */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              <span>Toutes les demandes ({items.length})</span>
              <div className="flex gap-1">
                {['tous', 'en_attente', 'signe'].map(f => (
                  <button key={f}
                          onClick={() => setFilter(f)}
                          className={`text-xs px-3 py-1 rounded ${filter === f ? 'bg-emerald-700 text-white' : 'bg-slate-100'}`}>
                    {f === 'tous' ? 'Tous' : f === 'signe' ? '✅ Signés' : '⏳ En attente'}
                  </button>
                ))}
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 max-h-[700px] overflow-auto">
            {filteredItems.length === 0 && (
              <p className="text-slate-500 text-sm text-center py-6">Aucun document.</p>
            )}
            {filteredItems.map(it => (
              <div key={it.id} className="p-3 border rounded hover:bg-slate-50" data-testid={`sig-item-${it.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {(DOC_TYPE_OPTIONS.find(o => o.value === it.doc_type) || {}).label || it.doc_type}
                      </Badge>
                      {it.statut === 'signe' ? (
                        <Badge className="bg-emerald-100 text-emerald-800 text-xs"><CheckCircle2 className="w-3 h-3 mr-1" />Signé</Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-800 text-xs"><Clock className="w-3 h-3 mr-1" />En attente</Badge>
                      )}
                      {it.mode_signature === 'face_a_face' && (
                        <Badge variant="outline" className="text-xs"><Smartphone className="w-3 h-3 mr-1" />F2F</Badge>
                      )}
                    </div>
                    <div className="font-semibold text-sm mt-1 truncate">{it.titre}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {it.client_nom && <span>{it.client_nom} · </span>}
                      {it.montant_ht && <span>{it.montant_ht}€ HT · </span>}
                      {it.destinataire_email && <span>{it.destinataire_email} · </span>}
                      <span>{new Date(it.created_at).toLocaleDateString('fr-FR')}</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    {it.statut !== 'signe' ? (
                      <Button size="sm" variant="outline"
                              onClick={() => copyLink(`${FRONTEND_URL}/signer/${it.token}`)}>
                        <Copy className="w-3 h-3 mr-1" /> Copier lien
                      </Button>
                    ) : (
                      it.drive_uploads?.[0]?.web_view_link && (
                        <a href={it.drive_uploads[0].web_view_link} target="_blank" rel="noopener noreferrer"
                           className="text-xs text-emerald-700 hover:underline flex items-center gap-1">
                          <ExternalLink className="w-3 h-3" /> Drive
                        </a>
                      )
                    )}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
